import { useLazyQuery } from '@apollo/client';
import { ClickAwayListener, Popper, TextField } from '@mui/material';
import { gql } from '__generated__/gql';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';

import debounce from 'utils/debounce';

import theme from './theme.module.scss';

type InvestmentEntity = {
  id: string;
  name: string;
};

const SEARCH_QUERY = gql(`
  query SearchInvestmentEntitiesForFundClosings($text: String!, $limit: Int) {
    search(text: $text, limit: $limit) {
      investmentEntities {
        nodes {
          id
          name
        }
      }
    }
  }
`);

const SearchInvestmentEntities: FC<{
  onChange: (investmentEntity: InvestmentEntity) => void;
  value?: InvestmentEntity;
  label?: string;
}> = ({ value, onChange, label = '' }) => {
  const [currentValue, setCurrentValue] = useState<InvestmentEntity | null>(value || null);
  const [getSearchResults, { loading, error, data, refetch, variables }] =
    useLazyQuery(SEARCH_QUERY);

  const ref = useRef<HTMLDivElement | null>(null);

  const performSearch = searchTerm =>
    getSearchResults({
      variables: {
        text: searchTerm.trim(),
        limit: 10,
      },
    });

  function results() {
    if (loading) return <LoadingIndicator />;

    if (error) return <ErrorMessage error={error} refetch={refetch} />;

    if (!data || !data.search || variables?.text === '') return null;

    const investmentEntities = data.search.investmentEntities.nodes;

    if (investmentEntities.length === 0) return <p>No results found</p>;

    return (
      <>
        {investmentEntities.map(investmentEntity => (
          <div
            key={investmentEntity.id}
            className={theme.investmentEntity}
            onClick={() => {
              setCurrentValue(investmentEntity);
              onChange(investmentEntity);
            }}
          >
            <span className={theme.name}>{investmentEntity.name}</span>
          </div>
        ))}
      </>
    );
  }

  if (currentValue) {
    return (
      <>
        <p>{currentValue.name}</p>
        <Button onClick={() => setCurrentValue(null)}>Clear</Button>
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => performSearch('')}>
      <div className={theme.container}>
        <TextField
          ref={ref}
          className={theme.input}
          fullWidth
          size="small"
          label={label}
          onBlur={e => (e.target.value = '')}
          onChange={debounce(e => performSearch(e.target.value), 500)}
          placeholder="Find by name"
        />
        <Popper
          open={true}
          style={{ width: ref.current?.offsetWidth, zIndex: 10000 }}
          anchorEl={ref.current}
          placement="bottom-start"
          onClick={e => e.preventDefault()}
        >
          <div className={theme.resultsContainer}>{results()}</div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default SearchInvestmentEntities;
